import { createFileRoute, Link } from "@tanstack/react-router";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  computeProjection,
  computeMonthlyProjection,
  DEFAULT_INPUTS,
  fmtKr,
  fmtNum,
  fmtPct,
  type CalcInputs,
  type EquipmentMode,
  type ProjectionSummary,
} from "../lib/atomika-calc";
import { api, type Scenario } from "../lib/api";
import {
  ORG_META,
  type Company,
  type OrgId,
  type StageMeta,
} from "../lib/markadur";
import {
  SavingsChart,
  type ChartMode,
  type ChartPoint,
} from "../components/reiknivel/SavingsChart";
import {
  CostReductionChart,
  type CostPoint,
} from "../components/reiknivel/CostReductionChart";
import { RummetraMaeling } from "../components/reiknivel/RummetraMaeling";

export const Route = createFileRoute("/verkfaeri/atomika-reiknivel")({
  component: CalculatorPage,
});

// Brandslitir Atomika (frumtýpan)
const ATOMIKA_BLUE = "#045AFF";
const ATOMIKA_BLUE_DARK = "#002B62";
const ATOMIKA_BLUE_DEEP = "#000A1C";

const C_GROSS = "#002B62";
const C_EQUIPMENT = "#94A3B8";
const C_COMPANY = "#045AFF";
const C_CUSTOMER = "#21CE4E";
const C_CUSTOMER_TEXT = "#15803D";

const MONTHLY_PRESETS = [
  200_000, 300_000, 500_000, 750_000, 1_000_000, 1_500_000, 2_000_000,
  2_500_000, 3_000_000, 3_500_000, 4_000_000, 4_500_000,
];

function fmtPreset(n: number): string {
  if (n >= 1_000_000) {
    const v = n / 1_000_000;
    return `${(Number.isInteger(v) ? String(v) : v.toFixed(1)).replace(".", ",")}M`;
  }
  return `${Math.round(n / 1000)}K`;
}

const WATER_DEFAULTS = { color: "#00b5e2", scale: 16, speed: 10, opacity: 1 };

const BLUE_SWATCHES = [
  "#CDEBFB",
  "#9CD3F0",
  "#6FBDE8",
  "#3E8BC0",
  "#2C77A8",
  "#00B5E2",
  "#00B4D8",
  "#0096C7",
  "#0077B6",
  "#1472FF",
  "#045AFF",
  "#1E5C8A",
  "#0F4C75",
  "#062A52",
  "#002B62",
];

const IS_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "Maí",
  "Jún",
  "Júl",
  "Ágú",
  "Sep",
  "Okt",
  "Nóv",
  "Des",
];
function monthYearLabel(d: Date): string {
  return `${IS_MONTHS[d.getMonth()]}-${String(d.getFullYear()).slice(-2)}`;
}

type Tab = "reiknivel" | "rummetra" | "innri" | "aaetlanir";
type ViewMode = "laekkun" | "ar" | "manudir";

const BEFORE_YEARS = 3;

type PickedCompany = { kennitala: string; name: string };

function CalculatorPage() {
  const [tab, setTab] = useState<Tab>("reiknivel");
  const [inputs, setInputs] = useState<CalcInputs>(DEFAULT_INPUTS);
  const [pickedCompany, setPickedCompany] = useState<PickedCompany | null>(
    null,
  );
  const [cardOrder, setCardOrder] = useState<string[]>([
    "heild",
    "ar1",
    "brutto",
    "bunadur",
  ]);
  const [crmOpen, setCrmOpen] = useState(false);

  // Innri
  const [laufidEnabled, setLaufidEnabled] = useState(false);
  const [laufidPct, setLaufidPct] = useState(30);
  const [atomikaHours, setAtomikaHours] = useState(25);
  const [atomikaHourRate, setAtomikaHourRate] = useState(16000);
  const [discountRate, setDiscountRate] = useState(8);
  const [showMonthlyRevenue, setShowMonthlyRevenue] = useState(false);
  const [actualSavingsPct, setActualSavingsPct] = useState(22);

  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [view, setView] = useState<ViewMode>("laekkun");

  const [showWaterControls, setShowWaterControls] = useState(false);
  const [waterColor, setWaterColor] = useState(WATER_DEFAULTS.color);
  const [waveScale, setWaveScale] = useState(WATER_DEFAULTS.scale);
  const [waveSpeed, setWaveSpeed] = useState(WATER_DEFAULTS.speed);
  const [waterOpacity, setWaterOpacity] = useState(WATER_DEFAULTS.opacity);

  const resetWater = () => {
    setWaterColor(WATER_DEFAULTS.color);
    setWaveScale(WATER_DEFAULTS.scale);
    setWaveSpeed(WATER_DEFAULTS.speed);
    setWaterOpacity(WATER_DEFAULTS.opacity);
  };

  const cardSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const handleCardDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setCardOrder((prev) => {
      const from = prev.indexOf(String(active.id));
      const to = prev.indexOf(String(over.id));
      if (from === -1 || to === -1) return prev;
      return arrayMove(prev, from, to);
    });
  };

  const projection = useMemo(() => computeProjection(inputs), [inputs]);
  const { rows, summary } = projection;
  const companySharePct = 100 - inputs.customerSharePct;

  const yearPoints = useMemo<ChartPoint[]>(
    () =>
      rows.map((r) => ({
        x: r.year,
        performancePct: r.performancePct,
        grossSavings: r.grossSavings,
        equipmentPayment: r.equipmentPayment,
        netSavings: r.netSavings,
        customerBenefit: r.customerBenefit,
        companyRevenue: r.companyRevenue,
      })),
    [rows],
  );

  const monthPoints = useMemo<ChartPoint[]>(
    () =>
      computeMonthlyProjection(inputs).map((p) => ({
        x: p.month,
        performancePct: p.performancePct,
        grossSavings: p.grossSavings,
        equipmentPayment: p.equipmentPayment,
        netSavings: p.netSavings,
        customerBenefit: p.customerBenefit,
        companyRevenue: p.companyRevenue,
      })),
    [inputs],
  );

  const { reductionPoints, momentLabel } = useMemo(() => {
    const baseline = summary.annualCost;
    const now = new Date();
    const pts: CostPoint[] = [];

    for (let i = BEFORE_YEARS; i >= 1; i--) {
      const d = new Date(now);
      d.setFullYear(now.getFullYear() - i);
      pts.push({
        label: monthYearLabel(d),
        paid: baseline,
        atomikaBenefit: 0,
        savings: 0,
        baseline,
        isBefore: true,
      });
    }

    const moment = monthYearLabel(now);
    pts.push({
      label: moment,
      paid: baseline,
      atomikaBenefit: 0,
      savings: 0,
      baseline,
      isBefore: true,
    });

    rows.forEach((r) => {
      const d = new Date(now);
      d.setFullYear(now.getFullYear() + r.year);
      const atomikaBenefit = r.companyRevenue + r.equipmentPayment;
      pts.push({
        label: monthYearLabel(d),
        paid: Math.max(0, baseline - r.grossSavings),
        atomikaBenefit,
        savings: r.customerBenefit,
        baseline,
        isBefore: false,
      });
    });

    return { reductionPoints: pts, momentLabel: moment };
  }, [summary.annualCost, rows]);

  const setField = (key: keyof CalcInputs, value: number | string) => {
    setInputs((prev) => ({ ...prev, [key]: value }));
  };

  const handleNum =
    (key: keyof CalcInputs) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value === "" ? 0 : Number(e.target.value);
      setField(key, Number.isNaN(v) ? 0 : v);
    };

  const setHorizon = (n: number) => {
    setInputs((prev) => {
      const h = Math.max(1, Math.min(20, Math.round(n)));
      const perf = Array.from({ length: h }, () => 100);
      return { ...prev, horizonYears: h, performanceByYear: perf };
    });
  };

  const refreshScenarios = useCallback(() => {
    api
      .listScenarios()
      .then(setScenarios)
      .catch(() => {});
  }, []);

  useEffect(() => {
    refreshScenarios();
  }, [refreshScenarios]);

  const handleSave = () => {
    if (!inputs.customerName.trim()) {
      setSaveMsg("Villa: Sláðu inn heiti viðskiptavinar fyrst");
      setTimeout(() => setSaveMsg(null), 2500);
      return;
    }
    setSaving(true);
    setSaveMsg(null);
    api
      .saveScenario({
        customerName: inputs.customerName,
        notes: notes || null,
        inputs,
        results: summary,
      })
      .then(() => {
        setSaveMsg("Áætlun vistuð ✓");
        refreshScenarios();
        setTimeout(() => setSaveMsg(null), 2500);
      })
      .catch((e: unknown) =>
        setSaveMsg(`Villa: ${e instanceof Error ? e.message : String(e)}`),
      )
      .finally(() => setSaving(false));
  };

  const handleLoad = (loaded: CalcInputs, customerName: string) => {
    setInputs({ ...DEFAULT_INPUTS, ...loaded, customerName });
    setTab("reiknivel");
  };

  const handleDelete = (id: number) => {
    setScenarios((list) => list.filter((s) => s.id !== id));
    api.deleteScenario(id).catch(() => refreshScenarios());
  };

  const openCrm = () => {
    if (!inputs.customerName.trim()) {
      setSaveMsg("Villa: Sláðu inn heiti viðskiptavinar fyrst");
      setTimeout(() => setSaveMsg(null), 2500);
      return;
    }
    setCrmOpen(true);
  };

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
      {/* Hero */}
      <div
        className="relative mb-6 overflow-hidden rounded-2xl border border-blue-100 shadow-sm"
        style={{
          background:
            "linear-gradient(135deg, #ffffff 0%, #EAF6FD 55%, #d8e9fb 100%)",
        }}
      >
        <div
          className="absolute -right-20 -top-24 h-80 w-80 rounded-full opacity-20"
          style={{
            background: "radial-gradient(circle, #045AFF 0%, transparent 70%)",
          }}
          aria-hidden
        />
        <div
          className="absolute -bottom-28 right-10 h-80 w-80 rounded-full opacity-10"
          style={{
            background: "radial-gradient(circle, #21CE4E 0%, transparent 70%)",
          }}
          aria-hidden
        />
        <div className="relative flex flex-wrap items-center gap-6 px-6 py-8 sm:px-10 sm:py-9">
          <div
            className="flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-2xl text-5xl shadow-lg sm:h-24 sm:w-24 sm:text-6xl"
            style={{
              background: "linear-gradient(135deg, #045AFF 0%, #000A1C 100%)",
            }}
          >
            <span aria-hidden>💧</span>
          </div>
          <div className="min-w-[260px] flex-1">
            <p
              className="mb-1 text-[11px] font-semibold uppercase tracking-[0.25em] sm:text-xs"
              style={{ color: ATOMIKA_BLUE_DARK }}
            >
              Innri sölu-reiknivél
            </p>
            <h1
              className="mb-2 font-black leading-none tracking-tight"
              style={{
                fontSize: "clamp(2rem, 4.5vw, 3.25rem)",
                color: ATOMIKA_BLUE_DEEP,
                letterSpacing: "-0.02em",
              }}
            >
              Atomika reiknivél
            </h1>
            <p
              className="text-sm font-medium sm:text-base"
              style={{ color: "#002B62b3" }}
            >
              Sparnaðurinn greiðir búnaðinn, svo skiptist nettó ávinningurinn.
            </p>
          </div>
          <div className="hidden items-center gap-6 border-l border-blue-200 pl-6 lg:flex">
            <HeroStat
              label="Ávinningur viðsk. 1. ár"
              value={fmtKr(summary.year1CustomerBenefit)}
              color={C_CUSTOMER_TEXT}
            />
            <HeroStat
              label={`Ávinningur viðsk. á ${inputs.horizonYears} árum`}
              value={fmtKr(summary.totalCustomerBenefit)}
              color={C_CUSTOMER_TEXT}
              highlight
            />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <nav className="flex gap-1">
          {(
            [
              ["reiknivel", "Verðskrá byggð á árangri"],
              ["rummetra", "Rúmmetra mæling"],
            ] as [Tab, string][]
          ).map(([id, label]) => {
            const isActive = id === tab;
            return (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={`whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "text-white shadow-sm"
                    : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                }`}
                style={isActive ? { backgroundColor: ATOMIKA_BLUE } : {}}
              >
                {label}
              </button>
            );
          })}
          <button
            onClick={() => setTab("innri")}
            className={`flex items-center gap-1.5 whitespace-nowrap rounded-lg border px-3.5 py-2 text-sm font-medium transition-colors ${
              tab === "innri"
                ? "border-transparent text-white shadow-sm"
                : "border-slate-200 text-slate-400 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700"
            }`}
            style={tab === "innri" ? { backgroundColor: "#334155" } : {}}
            title="Innri mælaborð — ekki sýna viðskiptavinum"
          >
            <svg
              className="h-3.5 w-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            Innri
          </button>
        </nav>

        <button
          onClick={() =>
            setTab(tab === "aaetlanir" ? "reiknivel" : "aaetlanir")
          }
          className={`inline-flex items-center gap-2 whitespace-nowrap rounded-lg border px-3.5 py-2 text-sm font-medium transition-colors ${
            tab === "aaetlanir"
              ? "border-slate-300 bg-slate-100 text-slate-800"
              : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50"
          }`}
        >
          {tab === "aaetlanir" ? "Til baka" : "Vistaðar áætlanir"}
          {scenarios.length > 0 && tab !== "aaetlanir" && (
            <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-slate-100 px-1.5 text-[11px] font-semibold text-slate-600">
              {scenarios.length}
            </span>
          )}
        </button>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.18 }}
        >
          {tab === "reiknivel" ? (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[380px_1fr]">
              {/* Inputs */}
              <div className="h-fit rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h2 className="mb-4 flex items-center gap-2 font-semibold text-slate-900">
                  <span
                    className="h-5 w-1.5 rounded-full"
                    style={{ backgroundColor: ATOMIKA_BLUE }}
                  />
                  Forsendur
                </h2>

                <div className="space-y-4">
                  <Field label="Viðskiptavinur">
                    <CustomerPicker
                      value={inputs.customerName}
                      onNameChange={(v) => {
                        setField("customerName", v);
                        setPickedCompany(null);
                      }}
                      onPick={(c) => {
                        setField("customerName", c.name);
                        setPickedCompany(c);
                      }}
                    />
                  </Field>

                  <div>
                    <NumField
                      label="Mánaðarkostnaður heits vatns"
                      suffix="kr/mán"
                      value={inputs.monthlyCost}
                      onChange={handleNum("monthlyCost")}
                    />
                    <div className="mt-2 flex flex-wrap justify-center gap-1.5">
                      {MONTHLY_PRESETS.map((amount) => {
                        const isActive = inputs.monthlyCost === amount;
                        return (
                          <button
                            key={amount}
                            type="button"
                            onClick={() => setField("monthlyCost", amount)}
                            className={`rounded-md border px-2.5 py-1 text-xs font-semibold tabular-nums transition-colors ${
                              isActive
                                ? "border-transparent text-white shadow-sm"
                                : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                            }`}
                            style={
                              isActive ? { backgroundColor: ATOMIKA_BLUE } : {}
                            }
                          >
                            {fmtPreset(amount)}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <SliderField
                    label="Áætluð lækkun með Atomika"
                    suffix="%"
                    value={inputs.savingsPct}
                    min={0}
                    max={80}
                    step={1}
                    onChange={(v) => setField("savingsPct", v)}
                  />

                  <Field label="Samningstími">
                    <div className="relative">
                      <input
                        type="number"
                        inputMode="numeric"
                        min={1}
                        max={20}
                        step={1}
                        value={inputs.horizonYears}
                        onChange={(e) =>
                          setHorizon(
                            e.target.value === "" ? 1 : Number(e.target.value),
                          )
                        }
                        className="w-full appearance-none rounded-lg border border-slate-200 px-3 py-2 pr-10 text-sm tabular-nums focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                      />
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-slate-400">
                        ár
                      </span>
                    </div>
                  </Field>

                  <SliderField
                    label={`Hlutur viðskiptavinar (Atomika ${companySharePct}%)`}
                    suffix="%"
                    value={inputs.customerSharePct}
                    min={0}
                    max={100}
                    step={5}
                    onChange={(v) => setField("customerSharePct", v)}
                  />

                  <div className="space-y-3 rounded-xl border border-slate-100 bg-slate-50 p-3.5">
                    <p className="text-sm font-semibold text-slate-800">
                      Búnaður
                    </p>

                    <div className="space-y-1 rounded-lg bg-slate-200/60 p-1">
                      {(
                        [
                          [
                            "sparnadur",
                            "Greitt með sparnaði",
                            "Sparnaður greiðir búnaðinn upp, svo skiptum við",
                          ],
                          [
                            "vidskiptavinur",
                            "Viðskiptavinur kaupir",
                            "Eingreiðsla í byrjun, utan reiknivélar",
                          ],
                        ] as [EquipmentMode, string, string][]
                      ).map(([id, label, hint]) => {
                        const active = inputs.equipmentMode === id;
                        return (
                          <button
                            key={id}
                            type="button"
                            onClick={() => setField("equipmentMode", id)}
                            className={`w-full rounded-md px-3 py-1.5 text-left transition-colors ${
                              active
                                ? "text-white shadow-sm"
                                : "text-slate-600 hover:bg-white/60 hover:text-slate-900"
                            }`}
                            style={
                              active ? { backgroundColor: ATOMIKA_BLUE } : {}
                            }
                          >
                            <span className="block text-xs font-semibold leading-tight">
                              {label}
                            </span>
                            <span
                              className={`block text-[10px] leading-tight ${
                                active ? "text-white/75" : "text-slate-400"
                              }`}
                            >
                              {hint}
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    <NumField
                      label="Kerfiskostnaður (heild)"
                      suffix="kr"
                      value={inputs.systemCost}
                      onChange={handleNum("systemCost")}
                    />

                    {inputs.equipmentMode === "sparnadur" ? (
                      <>
                        <NumField
                          label="Uppgreiðslumánuðar (hámark)"
                          suffix="mán"
                          value={inputs.payoffMonths}
                          onChange={handleNum("payoffMonths")}
                        />
                        <div className="space-y-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2.5">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-500">
                              Greitt upp úr sparnaði
                            </span>
                            <span className="font-semibold tabular-nums text-slate-700">
                              {fmtKr(summary.equipmentRecovered)} /{" "}
                              {fmtKr(inputs.systemCost)}
                            </span>
                          </div>
                          {summary.payoffMonthActual != null ? (
                            <p
                              className="text-[11px] font-medium"
                              style={{ color: "#15803D" }}
                            >
                              ✓ Uppgreitt eftir {summary.payoffMonthActual} mán
                              — svo skiptist {inputs.customerSharePct}/
                              {companySharePct}.
                            </p>
                          ) : (
                            <p
                              className="text-[11px] font-medium"
                              style={{ color: "#d97706" }}
                            >
                              ⚠ Næst ekki að greiða upp á {inputs.payoffMonths}{" "}
                              mán — vantar{" "}
                              {fmtKr(
                                Math.max(
                                  0,
                                  inputs.systemCost -
                                    summary.equipmentRecovered,
                                ),
                              )}
                              .
                            </p>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="rounded-lg border border-blue-100 bg-white px-3 py-2.5">
                        <div className="flex items-center justify-between">
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                            Eingreiðsla í byrjun
                          </p>
                          <p
                            className="text-base font-bold leading-tight tabular-nums"
                            style={{ color: ATOMIKA_BLUE }}
                          >
                            {fmtKr(inputs.systemCost)}
                          </p>
                        </div>
                        <p className="mt-1 text-[11px] leading-snug text-slate-500">
                          Viðskiptavinur greiðir búnaðinn í byrjun — fer ekki
                          inn í reiknivélina. Sparnaðurinn skiptist{" "}
                          {inputs.customerSharePct}/{companySharePct} frá fyrsta
                          ári.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-5 border-t border-slate-100 pt-5">
                  <Field label="Athugasemd (valfrjálst)">
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={2}
                      placeholder="t.d. tilboð sent, fundur bókaður…"
                      className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    />
                  </Field>
                  <button
                    onClick={openCrm}
                    className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold text-white shadow-sm transition-colors"
                    style={{
                      background: `linear-gradient(135deg, ${ATOMIKA_BLUE} 0%, ${ATOMIKA_BLUE_DARK} 100%)`,
                    }}
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                      />
                    </svg>
                    Stofna sölutækifæri í CRM →
                  </button>
                  <p className="mt-1.5 text-center text-[11px] text-slate-400">
                    Sendir tilboðið beint í Atomika CRM sölupípuna með
                    sparnaðarforsendunum.
                  </p>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="mt-3 w-full rounded-lg border-2 py-2.5 text-sm font-semibold transition-colors hover:bg-slate-50 disabled:opacity-60"
                    style={{ borderColor: "#e2e8f0", color: "#475569" }}
                  >
                    {saving ? "Vista…" : "Vista áætlun (drög)"}
                  </button>
                  {saveMsg && (
                    <p
                      className={`mt-2 text-center text-sm ${
                        saveMsg.startsWith("Villa")
                          ? "text-red-600"
                          : "text-green-600"
                      }`}
                    >
                      {saveMsg}
                    </p>
                  )}
                </div>
              </div>

              {/* Results + chart */}
              <div className="space-y-6">
                {(() => {
                  const cardProps: Record<
                    string,
                    {
                      label: string;
                      value: string;
                      sub?: string;
                      accent: string;
                      highlight?: boolean;
                    }
                  > = {
                    brutto: {
                      label: "Brúttó sparnaður",
                      value: `${fmtKr(summary.baseMonthlySaving)}/mán`,
                      sub: `${fmtKr(summary.baseAnnualSavings)}/ár · ${fmtPct(
                        inputs.savingsPct,
                      )} sparnaður`,
                      accent: C_GROSS,
                    },
                    bunadur:
                      inputs.equipmentMode === "sparnadur"
                        ? {
                            label: "Greitt í búnað",
                            value: fmtKr(summary.equipmentRecovered),
                            sub:
                              summary.payoffMonthActual != null
                                ? `Uppgreitt eftir ${summary.payoffMonthActual} mán · engir vextir`
                                : `Vantar ${fmtKr(
                                    Math.max(
                                      0,
                                      inputs.systemCost -
                                        summary.equipmentRecovered,
                                    ),
                                  )}`,
                            accent: C_EQUIPMENT,
                          }
                        : {
                            label: "Búnaður (eingreiðsla)",
                            value: fmtKr(inputs.systemCost),
                            sub: "Greitt í byrjun · utan reiknivélar",
                            accent: C_EQUIPMENT,
                          },
                    ar1: {
                      label: "Ávinningur viðsk. 1. ár",
                      value: `${fmtKr(summary.year1CustomerMonthly)}/mán`,
                      sub: `${fmtKr(summary.year1CustomerBenefit)}/ár · ${
                        inputs.customerSharePct
                      }% af nettó`,
                      accent: C_CUSTOMER,
                      highlight: true,
                    },
                    heild: {
                      label: `Ávinningur viðsk. á ${inputs.horizonYears} árum`,
                      value: fmtKr(summary.totalCustomerBenefit),
                      sub: `Atomika fær ${fmtKr(summary.totalCompanyRevenue)}`,
                      accent: C_CUSTOMER,
                      highlight: true,
                    },
                  };
                  return (
                    <DndContext
                      sensors={cardSensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleCardDragEnd}
                    >
                      <SortableContext
                        items={cardOrder}
                        strategy={rectSortingStrategy}
                      >
                        <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
                          {cardOrder.map((id) => (
                            <SortableResultCard
                              key={id}
                              id={id}
                              {...cardProps[id]}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  );
                })()}

                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <h2 className="font-semibold text-slate-900">
                        {view === "laekkun"
                          ? "Lækkun kostnaðar með Atomika"
                          : "Skipting brúttó sparnaðar"}
                      </h2>
                      <div className="inline-flex rounded-lg bg-slate-100 p-0.5">
                        {(
                          [
                            ["laekkun", "Lækkun"],
                            ["ar", "Ár"],
                            ["manudir", "Mánuðir"],
                          ] as [ViewMode, string][]
                        ).map(([id, label]) => {
                          const isActive = id === view;
                          return (
                            <button
                              key={id}
                              onClick={() => setView(id)}
                              className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                                isActive
                                  ? "text-white shadow-sm"
                                  : "text-slate-500 hover:text-slate-800"
                              }`}
                              style={
                                isActive
                                  ? { backgroundColor: ATOMIKA_BLUE }
                                  : {}
                              }
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-[11px] text-slate-500">
                      {view === "laekkun" ? (
                        <>
                          <LegendDot
                            color={C_EQUIPMENT}
                            label="Án Atomika"
                            dashed
                          />
                          <LegendDot color={C_GROSS} label="Með Atomika" />
                          <LegendDot
                            color={C_CUSTOMER}
                            label="Þinn sparnaður"
                          />
                        </>
                      ) : (
                        <>
                          <LegendDot
                            color={C_GROSS}
                            label="Brúttó sparnaður"
                            dashed
                          />
                          {inputs.equipmentMode !== "vidskiptavinur" && (
                            <LegendDot color={C_EQUIPMENT} label="Í búnað" />
                          )}
                          <LegendDot
                            color={C_COMPANY}
                            label={`Atomika ${companySharePct}%`}
                          />
                          <LegendDot
                            color={C_CUSTOMER}
                            label={`Viðskiptavinur ${inputs.customerSharePct}%`}
                          />
                        </>
                      )}
                    </div>
                  </div>
                  <p className="mb-3 text-xs text-slate-500">
                    {view === "laekkun"
                      ? `Heildarkostnaður heits vatns yfir tíma. Fyrirtækið hefur borgað fullt verð (${fmtKr(
                          summary.annualCost,
                        )}/ár) — Atomika kemur inn í ${momentLabel} og þá fyrst hefst lækkunin um ${fmtPct(
                          inputs.savingsPct,
                        )}. Græna svæðið er ávinningur viðskiptavinarins.`
                      : view === "manudir"
                        ? `Mánaðarsýn — 100% af sparnaðinum greiðir búnaðinn fyrstu ${
                            summary.payoffMonthActual ?? inputs.payoffMonths
                          } mánuðina (grátt), svo skiptist nettóin ${
                            inputs.customerSharePct
                          }/${companySharePct}.`
                        : inputs.equipmentMode === "sparnadur"
                          ? `Fyrstu ${
                              summary.payoffMonthActual ?? inputs.payoffMonths
                            } mánuðina fer allur sparnaðurinn í að greiða búnaðinn (grátt, engir vextir) — eftir uppgreiðslu skiptist hann ${
                              inputs.customerSharePct
                            }/${companySharePct}.`
                          : "Búnaður greiddur í byrjun af viðskiptavini — allur sparnaðurinn skiptist beint, engin afborgun dregst frá."}
                  </p>
                  {view === "laekkun" ? (
                    <CostReductionChart
                      data={reductionPoints}
                      startLabel={momentLabel}
                      waterColor={waterColor}
                      waveScale={waveScale}
                      waveSpeed={waveSpeed}
                      waterOpacity={waterOpacity}
                    />
                  ) : (
                    <SavingsChart
                      data={view === "ar" ? yearPoints : monthPoints}
                      mode={view as ChartMode}
                      waveScale={waveScale}
                      waveSpeed={waveSpeed}
                    />
                  )}

                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => setShowWaterControls((v) => !v)}
                      className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                        showWaterControls
                          ? "border-slate-300 bg-slate-100 text-slate-700"
                          : "border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:bg-slate-50"
                      }`}
                    >
                      Grafstillingar
                    </button>
                  </div>

                  {showWaterControls && (
                    <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-3 rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                        Stilla öldu
                      </span>

                      {view === "laekkun" && (
                        <div className="flex items-center gap-2 text-xs text-slate-600">
                          Litur
                          <div className="flex flex-wrap items-center gap-1">
                            {BLUE_SWATCHES.map((hex) => {
                              const active =
                                waterColor.toLowerCase() === hex.toLowerCase();
                              return (
                                <button
                                  key={hex}
                                  type="button"
                                  onClick={() => setWaterColor(hex)}
                                  title={hex}
                                  className={`h-5 w-5 rounded-full border transition-transform ${
                                    active
                                      ? "scale-110 border-white ring-2 ring-slate-400 ring-offset-1"
                                      : "border-slate-200 hover:scale-110"
                                  }`}
                                  style={{ backgroundColor: hex }}
                                />
                              );
                            })}
                          </div>
                          <input
                            type="color"
                            value={waterColor}
                            onChange={(e) => setWaterColor(e.target.value)}
                            className="h-6 w-8 cursor-pointer rounded border border-slate-200 bg-white p-0"
                            title="Fínstilltu tón"
                          />
                          <span className="font-mono text-[11px] tabular-nums text-slate-400">
                            {waterColor}
                          </span>
                        </div>
                      )}

                      <label className="flex items-center gap-2 text-xs text-slate-600">
                        Gáruhæð
                        <input
                          type="range"
                          min={0}
                          max={20}
                          step={1}
                          value={waveScale}
                          onChange={(e) => setWaveScale(Number(e.target.value))}
                          className="w-28 accent-blue-600"
                        />
                        <span className="w-6 text-right font-mono text-[11px] tabular-nums text-slate-500">
                          {waveScale}
                        </span>
                      </label>

                      <label className="flex items-center gap-2 text-xs text-slate-600">
                        Hraði
                        <input
                          type="range"
                          min={4}
                          max={40}
                          step={1}
                          value={waveSpeed}
                          onChange={(e) => setWaveSpeed(Number(e.target.value))}
                          className="w-28 accent-blue-600"
                        />
                        <span className="w-10 text-right font-mono text-[11px] tabular-nums text-slate-500">
                          {waveSpeed}s
                        </span>
                      </label>

                      {view === "laekkun" && (
                        <label className="flex items-center gap-2 text-xs text-slate-600">
                          Styrkur
                          <input
                            type="range"
                            min={0.1}
                            max={1}
                            step={0.05}
                            value={waterOpacity}
                            onChange={(e) =>
                              setWaterOpacity(Number(e.target.value))
                            }
                            className="w-28 accent-blue-600"
                          />
                          <span className="w-10 text-right font-mono text-[11px] tabular-nums text-slate-500">
                            {Math.round(waterOpacity * 100)}%
                          </span>
                        </label>
                      )}

                      <div className="ml-auto flex items-center gap-3">
                        <button
                          type="button"
                          onClick={resetWater}
                          className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-500 transition-colors hover:border-slate-300 hover:bg-slate-50"
                        >
                          Sjálfgefið
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Ársbreytt tafla */}
                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <h2 className="mb-3 font-semibold text-slate-900">
                    Sundurliðun eftir árum
                  </h2>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-100 text-left text-slate-500">
                          <th className="py-2 pr-3 font-medium">Ár</th>
                          <th className="px-3 py-2 text-right font-medium">
                            Afköst
                          </th>
                          <th className="px-3 py-2 text-right font-medium">
                            Brúttó sparn.
                          </th>
                          {inputs.equipmentMode !== "vidskiptavinur" && (
                            <th className="px-3 py-2 text-right font-medium">
                              Í búnað
                            </th>
                          )}
                          <th className="px-3 py-2 text-right font-medium">
                            Nettó
                          </th>
                          <th className="px-3 py-2 text-right font-medium">
                            Viðsk. {inputs.customerSharePct}%
                          </th>
                          <th className="py-2 pl-3 text-right font-medium">
                            Atomika {companySharePct}%
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r) => (
                          <tr
                            key={r.year}
                            className="border-b border-slate-50 last:border-0"
                          >
                            <td className="py-2 pr-3 font-medium text-slate-700">
                              {r.year}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-slate-500">
                              {r.performancePct}%
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                              {fmtKr(r.grossSavings)}
                            </td>
                            {inputs.equipmentMode !== "vidskiptavinur" && (
                              <td className="px-3 py-2 text-right tabular-nums text-slate-500">
                                −{fmtKr(r.equipmentPayment)}
                              </td>
                            )}
                            <td className="px-3 py-2 text-right font-medium tabular-nums text-slate-700">
                              {fmtKr(r.netSavings)}
                            </td>
                            <td
                              className="px-3 py-2 text-right font-semibold tabular-nums"
                              style={{ color: C_CUSTOMER_TEXT }}
                            >
                              {fmtKr(r.customerBenefit)}
                            </td>
                            <td
                              className="py-2 pl-3 text-right font-medium tabular-nums"
                              style={{ color: C_COMPANY }}
                            >
                              {fmtKr(r.companyRevenue)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-slate-200 font-semibold text-slate-800">
                          <td className="py-2 pr-3" colSpan={2}>
                            Samtals
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {fmtKr(summary.totalGrossSavings)}
                          </td>
                          {inputs.equipmentMode !== "vidskiptavinur" && (
                            <td className="px-3 py-2 text-right tabular-nums text-slate-500">
                              −
                              {fmtKr(
                                summary.totalGrossSavings -
                                  summary.totalNetSavings,
                              )}
                            </td>
                          )}
                          <td className="px-3 py-2 text-right tabular-nums">
                            {fmtKr(summary.totalNetSavings)}
                          </td>
                          <td
                            className="px-3 py-2 text-right tabular-nums"
                            style={{ color: C_CUSTOMER_TEXT }}
                          >
                            {fmtKr(summary.totalCustomerBenefit)}
                          </td>
                          <td
                            className="py-2 pl-3 text-right tabular-nums"
                            style={{ color: C_COMPANY }}
                          >
                            {fmtKr(summary.totalCompanyRevenue)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                <p className="px-1 text-[11px] text-slate-400">
                  {inputs.equipmentMode === "sparnadur"
                    ? `Líkan: 100% af sparnaðinum greiðir búnaðinn upp í allt að ${
                        inputs.payoffMonths
                      } mánuði (engir vextir). ${
                        summary.payoffMonthActual != null
                          ? `Uppgreitt eftir ${summary.payoffMonthActual} mán.`
                          : `Næst ekki að greiða upp á ${inputs.payoffMonths} mán.`
                      } Eftir uppgreiðslu skiptist sparnaðurinn ${
                        inputs.customerSharePct
                      }/${companySharePct} alla samningstímann.`
                    : `Líkan: viðskiptavinur kaupir búnaðinn í byrjun (utan reiknivélar), svo allur sparnaðurinn er nettó ávinningur sem skiptist ${inputs.customerSharePct}/${companySharePct} frá fyrsta ári.`}
                </p>
              </div>
            </div>
          ) : tab === "rummetra" ? (
            <RummetraMaeling
              companyName={inputs.customerName}
              monthlyCost={inputs.monthlyCost}
            />
          ) : tab === "innri" ? (
            <InnriTab
              inputs={inputs}
              summary={summary}
              rows={rows}
              monthPoints={monthPoints}
              companySharePct={companySharePct}
              laufidEnabled={laufidEnabled}
              setLaufidEnabled={setLaufidEnabled}
              laufidPct={laufidPct}
              setLaufidPct={setLaufidPct}
              atomikaHours={atomikaHours}
              setAtomikaHours={setAtomikaHours}
              atomikaHourRate={atomikaHourRate}
              setAtomikaHourRate={setAtomikaHourRate}
              discountRate={discountRate}
              setDiscountRate={setDiscountRate}
              showMonthlyRevenue={showMonthlyRevenue}
              setShowMonthlyRevenue={setShowMonthlyRevenue}
              actualSavingsPct={actualSavingsPct}
              setActualSavingsPct={setActualSavingsPct}
            />
          ) : (
            <SavedScenarios
              scenarios={scenarios}
              onLoad={handleLoad}
              onDelete={handleDelete}
            />
          )}
        </motion.div>
      </AnimatePresence>

      {crmOpen && (
        <SendToCrmModal
          customerName={inputs.customerName}
          horizonYears={inputs.horizonYears}
          savingsPct={inputs.savingsPct}
          paybackYears={
            summary.payoffMonthActual != null
              ? summary.payoffMonthActual / 12
              : null
          }
          summary={summary}
          initialCompany={pickedCompany}
          onClose={() => setCrmOpen(false)}
        />
      )}
    </div>
  );
}

// ─── Innri flipi ─────────────────────────────────────────────────────────────

function InnriTab({
  inputs,
  summary,
  rows,
  monthPoints,
  companySharePct,
  laufidEnabled,
  setLaufidEnabled,
  laufidPct,
  setLaufidPct,
  atomikaHours,
  setAtomikaHours,
  atomikaHourRate,
  setAtomikaHourRate,
  discountRate,
  setDiscountRate,
  showMonthlyRevenue,
  setShowMonthlyRevenue,
  actualSavingsPct,
  setActualSavingsPct,
}: {
  inputs: CalcInputs;
  summary: ProjectionSummary;
  rows: { year: number; companyRevenue: number }[];
  monthPoints: ChartPoint[];
  companySharePct: number;
  laufidEnabled: boolean;
  setLaufidEnabled: (v: (p: boolean) => boolean) => void;
  laufidPct: number;
  setLaufidPct: (v: number) => void;
  atomikaHours: number;
  setAtomikaHours: (v: number) => void;
  atomikaHourRate: number;
  setAtomikaHourRate: (v: number) => void;
  discountRate: number;
  setDiscountRate: (v: number) => void;
  showMonthlyRevenue: boolean;
  setShowMonthlyRevenue: (v: (p: boolean) => boolean) => void;
  actualSavingsPct: number;
  setActualSavingsPct: (v: number) => void;
}) {
  const horizonMonths = inputs.horizonYears * 12;
  const atomikaRevenue = summary.totalCompanyRevenue;
  const salesCost = Math.max(0, atomikaHours * atomikaHourRate);
  const framlegd = atomikaRevenue - salesCost;
  const perMonth = horizonMonths > 0 ? framlegd / horizonMonths : 0;
  const perYear = inputs.horizonYears > 0 ? framlegd / inputs.horizonYears : 0;
  const roi = salesCost > 0 ? (framlegd / salesCost) * 100 : null;

  const r = discountRate / 100;
  const npvRevenue = rows.reduce(
    (s, row) => s + row.companyRevenue / Math.pow(1 + r, row.year),
    0,
  );
  const npvFramlegd = npvRevenue - salesCost;
  const npvDiff =
    atomikaRevenue > 0
      ? ((npvRevenue - atomikaRevenue) / atomikaRevenue) * 100
      : 0;

  const revenueMonthFirst =
    monthPoints.findIndex((m) => m.companyRevenue > 0) + 1;

  const companyShareFrac = companySharePct / 100;
  const customerShareFrac = inputs.customerSharePct / 100;
  const annualGrossPromised =
    inputs.monthlyCost * 12 * (inputs.savingsPct / 100);
  const annualGrossActual = inputs.monthlyCost * 12 * (actualSavingsPct / 100);
  const grossDiff = annualGrossActual - annualGrossPromised;
  const atomikaAdjustment = grossDiff * companyShareFrac;
  const customerAdjustment = grossDiff * customerShareFrac;
  const isCredit = atomikaAdjustment < 0;

  const laufidShare = laufidEnabled ? atomikaRevenue * (laufidPct / 100) : 0;
  const atomikaEftirLaufid = laufidEnabled
    ? atomikaRevenue * (1 - laufidPct / 100) - salesCost
    : framlegd;
  const laufidPerMonth = horizonMonths > 0 ? laufidShare / horizonMonths : 0;

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div className="flex items-center gap-2 px-1">
        <svg
          className="h-4 w-4 text-slate-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
        <span className="text-sm font-medium text-slate-400">
          Innri mælaborð — ekki sýna viðskiptavinum
        </span>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <span className="text-base font-bold text-slate-900">
            Framlegð Atomika
          </span>
          <span className="text-xs font-normal text-slate-400">
            — yfir {inputs.horizonYears} ár ({horizonMonths} mánuðir)
          </span>
        </div>
        <div className="mb-4 space-y-1.5 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-slate-500">
              Tekjuhlutfall ({companySharePct}%) yfir {inputs.horizonYears} ár
            </span>
            <span className="font-medium tabular-nums text-slate-700">
              +{fmtKr(atomikaRevenue)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-500">
              − Sölu- og vinnustundakostnaður
            </span>
            <span className="font-medium tabular-nums text-red-500">
              −{fmtKr(salesCost)}
            </span>
          </div>
          <div className="flex items-center justify-between border-t border-slate-100 pt-1.5">
            <span className="font-semibold text-slate-800">
              Framlegð Atomika
            </span>
            <span
              className={`font-bold tabular-nums ${
                framlegd >= 0 ? "text-slate-900" : "text-red-500"
              }`}
            >
              {framlegd >= 0 ? "" : "−"}
              {fmtKr(Math.abs(framlegd))}
            </span>
          </div>
          <p className="pt-0.5 text-[11px] text-slate-400">
            Búnaður er greiddur af viðskiptavini og telst ekki með í framlegð
            Atomika.
          </p>
        </div>

        <div className="mb-4 grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-600">
              Vinnustundir (sala + uppsetn.)
            </span>
            <input
              type="number"
              min={0}
              value={atomikaHours}
              onChange={(e) =>
                setAtomikaHours(
                  e.target.value === ""
                    ? 0
                    : Math.max(0, Number(e.target.value)),
                )
              }
              className="w-full rounded-lg px-3 py-2 text-sm tabular-nums outline-none ring-1 ring-slate-200 focus:ring-2 focus:ring-slate-400"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-600">
              Taxti (kr/klst)
            </span>
            <input
              type="number"
              min={0}
              step={500}
              value={atomikaHourRate}
              onChange={(e) =>
                setAtomikaHourRate(
                  e.target.value === ""
                    ? 0
                    : Math.max(0, Number(e.target.value)),
                )
              }
              className="w-full rounded-lg px-3 py-2 text-sm tabular-nums outline-none ring-1 ring-slate-200 focus:ring-2 focus:ring-slate-400"
            />
          </label>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {[
            {
              label: "Framlegð samtals",
              val: framlegd,
              fmt: (v: number) => `${v >= 0 ? "" : "−"}${fmtKr(Math.abs(v))}`,
            },
            {
              label: "Per mánuð",
              val: perMonth,
              fmt: (v: number) => `${v >= 0 ? "" : "−"}${fmtKr(Math.abs(v))}`,
            },
            {
              label: roi !== null ? "ROI" : "Per ár",
              val: roi ?? perYear,
              fmt: (v: number) =>
                roi !== null
                  ? `${v >= 0 ? "+" : ""}${Math.round(v)}%`
                  : fmtKr(Math.abs(v)),
            },
          ].map(({ label, val, fmt }) => (
            <div
              key={label}
              className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5 text-center"
            >
              <p className="mb-0.5 text-[10px] uppercase tracking-wide text-slate-400">
                {label}
              </p>
              <p
                className={`text-base font-bold tabular-nums ${
                  val >= 0 ? "text-emerald-600" : "text-red-500"
                }`}
              >
                {fmt(val)}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-4 border-t border-slate-100 pt-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-medium text-slate-600">
              Núvirðing (ávöxtunarkrafa)
            </span>
            <span className="text-sm font-bold tabular-nums text-slate-700">
              {discountRate}%
            </span>
          </div>
          <input
            type="range"
            min={0}
            max={20}
            step={0.5}
            value={discountRate}
            onChange={(e) => setDiscountRate(Number(e.target.value))}
            className="h-2 w-full cursor-pointer appearance-none rounded-full accent-slate-500"
          />
          <div className="mb-3 mt-0.5 flex justify-between text-[10px] text-slate-400">
            <span>0%</span>
            <span>20%</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5 text-center">
              <p className="mb-0.5 text-[10px] uppercase tracking-wide text-slate-400">
                Núvirtar tekjur (NPV)
              </p>
              <p className="text-base font-bold tabular-nums text-slate-800">
                {fmtKr(npvRevenue)}
              </p>
              <p className="mt-0.5 text-[10px] text-slate-400">
                {npvDiff >= 0 ? "+" : ""}
                {npvDiff.toFixed(1)}% vs nafnvirði
              </p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5 text-center">
              <p className="mb-0.5 text-[10px] uppercase tracking-wide text-slate-400">
                Núvirt framlegð
              </p>
              <p
                className={`text-base font-bold tabular-nums ${
                  npvFramlegd >= 0 ? "text-emerald-600" : "text-red-500"
                }`}
              >
                {npvFramlegd >= 0 ? "" : "−"}
                {fmtKr(Math.abs(npvFramlegd))}
              </p>
              <p className="mt-0.5 text-[10px] text-slate-400">
                eftir sölukostnað
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <span className="font-semibold text-slate-900">
              Mánaðarleg tekjufærsla
            </span>
            <p className="mt-0.5 text-xs text-slate-400">
              Hvað Atomika tekjufærir hvern mánuð yfir samningstímann
            </p>
          </div>
          <button
            onClick={() => setShowMonthlyRevenue((v) => !v)}
            className="flex items-center gap-2 text-sm font-medium text-slate-600"
            aria-pressed={showMonthlyRevenue}
          >
            <span
              className={
                showMonthlyRevenue ? "text-blue-700" : "text-slate-400"
              }
            >
              {showMonthlyRevenue ? "Sýnt" : "Falið"}
            </span>
            <span
              className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ${
                showMonthlyRevenue ? "bg-blue-500" : "bg-slate-200"
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${
                  showMonthlyRevenue ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </span>
          </button>
        </div>

        {showMonthlyRevenue && (
          <div className="mt-4">
            {revenueMonthFirst > 1 && (
              <p className="mb-3 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-600">
                ⚠ Engin tekjufærsla fyrstu {revenueMonthFirst - 1} mánuðina —
                allur sparnaður fer í að greiða upp búnaðinn. Tekjufærsla hefst
                í mánuði {revenueMonthFirst}.
              </p>
            )}
            <div className="max-h-64 overflow-x-auto overflow-y-auto rounded-lg border border-slate-100">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50">
                  <tr className="border-b border-slate-100 text-left text-slate-500">
                    <th className="px-3 py-2 font-medium">Mán.</th>
                    <th className="px-3 py-2 text-right font-medium">Brúttó</th>
                    <th className="px-3 py-2 text-right font-medium">
                      Tekjufærsla Atomika
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {monthPoints.map((m) => (
                    <tr
                      key={m.x}
                      className={`border-b border-slate-50 last:border-0 ${
                        m.companyRevenue === 0 ? "text-slate-400" : ""
                      }`}
                    >
                      <td className="px-3 py-1.5 tabular-nums">{m.x}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">
                        {fmtKr(m.grossSavings)}
                      </td>
                      <td
                        className="px-3 py-1.5 text-right font-medium tabular-nums"
                        style={{
                          color: m.companyRevenue > 0 ? C_COMPANY : undefined,
                        }}
                      >
                        {m.companyRevenue > 0 ? fmtKr(m.companyRevenue) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-5 shadow-sm">
        <div className="mb-1 flex items-center gap-2">
          <span className="font-semibold text-slate-900">Ársleiðrétting</span>
        </div>
        <p className="mb-4 text-xs text-slate-500">
          Salan byggir á áætlaðri lækkun. Í árslok er gert upp við raunlækkun —
          kreditreikningur ef minna næst, viðbótarreikningur ef meira.
        </p>

        <div className="mb-4 grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-slate-100 bg-white px-3 py-2.5">
            <p className="mb-1 text-[10px] uppercase tracking-wide text-slate-400">
              Lofuð lækkun (áætlun)
            </p>
            <p className="text-lg font-bold tabular-nums text-slate-700">
              {inputs.savingsPct}%
            </p>
            <p className="mt-0.5 text-[10px] text-slate-400">
              {fmtKr(annualGrossPromised)}/ár brúttó
            </p>
          </div>
          <label className="block rounded-xl border border-amber-200 bg-white px-3 py-2.5">
            <span className="mb-1 block text-[10px] uppercase tracking-wide text-amber-600">
              Raunlækkun (mæld)
            </span>
            <div className="flex items-center gap-1">
              <input
                type="number"
                min={0}
                max={100}
                step={0.5}
                value={actualSavingsPct}
                onChange={(e) =>
                  setActualSavingsPct(
                    e.target.value === ""
                      ? 0
                      : Math.max(0, Math.min(100, Number(e.target.value))),
                  )
                }
                className="w-full bg-transparent text-lg font-bold tabular-nums text-slate-900 outline-none"
              />
              <span className="text-lg font-bold text-slate-400">%</span>
            </div>
            <p className="mt-0.5 text-[10px] text-slate-400">
              {fmtKr(annualGrossActual)}/ár brúttó
            </p>
          </label>
        </div>

        <div
          className={`rounded-xl border px-4 py-3 ${
            Math.abs(atomikaAdjustment) < 1
              ? "border-slate-200 bg-slate-50"
              : isCredit
                ? "border-red-200 bg-red-50"
                : "border-emerald-200 bg-emerald-50"
          }`}
        >
          <div className="mb-1.5 flex items-center justify-between text-sm">
            <span className="text-slate-500">
              Mismunur á brúttó sparnaði (árlega)
            </span>
            <span
              className={`font-medium tabular-nums ${
                grossDiff >= 0 ? "text-emerald-600" : "text-red-500"
              }`}
            >
              {grossDiff >= 0 ? "+" : "−"}
              {fmtKr(Math.abs(grossDiff))}
            </span>
          </div>
          <div className="mb-1.5 flex items-center justify-between text-sm">
            <span className="text-slate-500">
              Leiðrétting til viðskiptavinar ({inputs.customerSharePct}%)
            </span>
            <span className="font-medium tabular-nums text-slate-600">
              {customerAdjustment >= 0 ? "+" : "−"}
              {fmtKr(Math.abs(customerAdjustment))}
            </span>
          </div>
          <div className="mt-2 flex items-center justify-between border-t border-slate-200/60 pt-2">
            <span className="font-semibold text-slate-800">
              {Math.abs(atomikaAdjustment) < 1
                ? "Engin leiðrétting"
                : isCredit
                  ? "Kreditreikningur í árslok"
                  : "Viðbótarreikningur í árslok"}
            </span>
            <span
              className={`text-lg font-bold tabular-nums ${
                Math.abs(atomikaAdjustment) < 1
                  ? "text-slate-500"
                  : isCredit
                    ? "text-red-600"
                    : "text-emerald-600"
              }`}
            >
              {isCredit ? "−" : Math.abs(atomikaAdjustment) < 1 ? "" : "+"}
              {fmtKr(Math.abs(atomikaAdjustment))}
            </span>
          </div>
        </div>
        <p className="mt-2 text-[11px] text-slate-400">
          Leiðrétting Atomika = {companySharePct}% af mismun á árssparnaði.
          Byggir á árlegum brúttó sparnaði við full afköst.
        </p>
      </div>

      <div
        className={`rounded-2xl border p-5 shadow-sm transition-colors ${
          laufidEnabled
            ? "border-emerald-200 bg-gradient-to-br from-emerald-50 to-white"
            : "border-slate-200 bg-white"
        }`}
      >
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="font-semibold text-slate-900">Laufið umboð</span>
          </div>
          <button
            onClick={() => setLaufidEnabled((v) => !v)}
            className="flex items-center gap-2 text-sm font-medium text-slate-600"
            aria-pressed={laufidEnabled}
          >
            <span
              className={laufidEnabled ? "text-emerald-700" : "text-slate-400"}
            >
              {laufidEnabled ? "Laufið er söluaðili" : "Atomika selur beint"}
            </span>
            <span
              className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ${
                laufidEnabled ? "bg-emerald-500" : "bg-slate-200"
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${
                  laufidEnabled ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </span>
          </button>
        </div>

        {laufidEnabled ? (
          <div className="space-y-4">
            <div>
              <div className="mb-1 flex items-center justify-between">
                <span className="text-xs font-medium text-slate-600">
                  Umboðsþóknun Laufið
                </span>
                <span
                  className="text-sm font-bold tabular-nums"
                  style={{ color: "#10B981" }}
                >
                  {laufidPct}%
                </span>
              </div>
              <input
                type="range"
                min={5}
                max={50}
                step={1}
                value={laufidPct}
                onChange={(e) => setLaufidPct(Number(e.target.value))}
                className="h-2 w-full cursor-pointer appearance-none rounded-full accent-emerald-500"
              />
              <div className="mt-0.5 flex justify-between text-[10px] text-slate-400">
                <span>5%</span>
                <span>50%</span>
              </div>
            </div>

            <div className="space-y-1.5 border-t border-emerald-100 pt-2 text-sm">
              <div className="flex items-center justify-between text-slate-500">
                <span>Heildartekjur úr tekjuhlutfalli</span>
                <span className="font-medium tabular-nums text-slate-700">
                  {fmtKr(atomikaRevenue)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-emerald-700">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  Laufið fær ({laufidPct}%)
                </span>
                <span className="font-semibold tabular-nums text-emerald-700">
                  {fmtKr(laufidShare)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-blue-700">
                  <span className="h-2 w-2 rounded-full bg-blue-500" />
                  Atomika fær ({100 - laufidPct}%)
                </span>
                <span className="font-semibold tabular-nums text-blue-700">
                  {fmtKr(atomikaRevenue * (1 - laufidPct / 100))}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-emerald-200 bg-emerald-100/60 px-3 py-2.5 text-center">
                <p className="mb-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-700">
                  Laufið — per mánuð
                </p>
                <p className="text-base font-bold tabular-nums text-emerald-700">
                  {fmtKr(laufidPerMonth)}
                </p>
                <p className="mt-0.5 text-[10px] text-emerald-600">
                  Samtals {fmtKr(laufidShare)}
                </p>
              </div>
              <div className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2.5 text-center">
                <p className="mb-0.5 text-[10px] font-medium uppercase tracking-wide text-blue-600">
                  Atomika — framlegð
                </p>
                <p
                  className={`text-base font-bold tabular-nums ${
                    atomikaEftirLaufid >= 0 ? "text-blue-700" : "text-red-500"
                  }`}
                >
                  {atomikaEftirLaufid >= 0 ? "" : "−"}
                  {fmtKr(Math.abs(atomikaEftirLaufid))}
                </p>
                <p className="mt-0.5 text-[10px] text-blue-500">
                  Eftir Laufið + sölukostnað
                </p>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-400">
            Kveiktu á toggle til að sjá hvað Laufið myndi fá ef þeir selja
            þennan samning.
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Senda í CRM ─────────────────────────────────────────────────────────────

function SendToCrmModal({
  customerName,
  horizonYears,
  savingsPct,
  paybackYears,
  summary,
  initialCompany,
  onClose,
}: {
  customerName: string;
  horizonYears: number;
  savingsPct: number;
  paybackYears: number | null;
  summary: ProjectionSummary;
  initialCompany: PickedCompany | null;
  onClose: () => void;
}) {
  const [company, setCompany] = useState<PickedCompany | null>(initialCompany);
  const [ownerOrg, setOwnerOrg] = useState<OrgId>("atomika");
  const [ownerUser, setOwnerUser] = useState("");
  const [stages, setStages] = useState<StageMeta[]>([]);
  const [stage, setStage] = useState("tilbod");
  const [amount, setAmount] = useState<number>(summary.totalCompanyRevenue);
  const [prob, setProb] = useState<number>(60);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<number | null>(null);

  useEffect(() => {
    api
      .getMarkadurStages()
      .then((c) => setStages(c.stages))
      .catch(() => {});
  }, []);

  const weighted = Math.round(amount * (prob / 100));

  const notes = [
    "Úr reiknivél",
    `${savingsPct}% lækkun`,
    paybackYears != null ? `endurgreiðsla ${fmtNum(paybackYears, "ár")}` : null,
    `ávinningur viðskiptavinar ${fmtKr(summary.totalCustomerBenefit)} yfir ${horizonYears} ár`,
  ]
    .filter(Boolean)
    .join(" · ");

  const send = () => {
    if (!company) {
      setErr("Veldu fyrirtæki úr fyrirtækjaskrá");
      return;
    }
    setSaving(true);
    setErr(null);
    api
      .createOpportunity({
        companyKennitala: company.kennitala,
        companyName: company.name,
        title: `${customerName || company.name} — Atomika samningur`,
        ownerOrg,
        ownerUser: ownerUser || undefined,
        stage,
        amountKr: amount,
        probabilityPct: prob,
        notes,
      })
      .then((o) => setCreatedId(o.id))
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setSaving(false));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[520px] rounded-[20px] bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="m-0 font-display text-[20px] font-extrabold text-ink">
            Senda tilboð í Atomika CRM
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg px-2 py-1 text-[16px] text-muted-2 hover:bg-canvas"
          >
            ✕
          </button>
        </div>

        {createdId ? (
          <div className="text-center">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-ok-50 text-[26px] text-ok">
              ✓
            </div>
            <div className="font-display text-[17px] font-bold text-ink">
              Sölutækifæri stofnað í CRM
            </div>
            <div className="mt-1 text-[13px] text-muted-2">
              {company?.name} · {fmtKr(amount)} · {prob}% líkur
            </div>
            <div className="mt-5 flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 rounded-[12px] border border-line bg-white px-4 py-2.5 text-[13px] font-bold text-muted hover:bg-canvas"
              >
                Loka
              </button>
              <Link
                to="/verkfaeri/markadurinn"
                className="flex-1 rounded-[12px] bg-[#F0871E] px-4 py-2.5 text-center text-[13px] font-bold text-white hover:opacity-90"
              >
                Opna sölupípu
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-3">
              <ModalLabel>Fyrirtæki</ModalLabel>
              {company ? (
                <div className="flex items-center justify-between rounded-[12px] border border-line bg-canvas px-3.5 py-2.5">
                  <span className="font-display text-[14px] font-bold text-ink">
                    {company.name}
                  </span>
                  <button
                    onClick={() => setCompany(null)}
                    className="text-[12px] font-semibold text-muted-2 hover:text-brand"
                  >
                    Breyta
                  </button>
                </div>
              ) : (
                <CompanyPicker
                  initialQuery={customerName}
                  onPick={setCompany}
                />
              )}
            </div>

            <div className="mb-3">
              <ModalLabel>Teymi</ModalLabel>
              <div className="flex overflow-hidden rounded-[12px] border border-line">
                {(["laufid", "atomika"] as OrgId[]).map((o) => (
                  <button
                    key={o}
                    onClick={() => setOwnerOrg(o)}
                    className={`flex-1 px-3 py-2.5 text-[13px] font-bold ${
                      ownerOrg === o ? "text-white" : "bg-white text-muted"
                    }`}
                    style={
                      ownerOrg === o
                        ? { background: ORG_META[o].color }
                        : undefined
                    }
                  >
                    {ORG_META[o].label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-3 grid grid-cols-2 gap-3">
              <div>
                <ModalLabel>Sölumaður</ModalLabel>
                <input
                  value={ownerUser}
                  onChange={(e) => setOwnerUser(e.target.value)}
                  placeholder="Nafn"
                  className="w-full rounded-[12px] border border-line bg-white px-3.5 py-2.5 text-[14px] text-ink outline-none focus:border-brand"
                />
              </div>
              <div>
                <ModalLabel>Sölustig</ModalLabel>
                <select
                  value={stage}
                  onChange={(e) => setStage(e.target.value)}
                  className="w-full rounded-[12px] border border-line bg-white px-3 py-2.5 text-[14px] font-semibold text-ink outline-none focus:border-brand"
                >
                  {stages.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-3">
              <ModalLabel>Samningsupphæð Atomika (kr)</ModalLabel>
              <input
                type="number"
                value={amount}
                min={0}
                step={100000}
                onChange={(e) => setAmount(Math.max(0, Number(e.target.value)))}
                className="w-full rounded-[12px] border border-line bg-white px-3.5 py-2.5 text-[14px] font-semibold text-ink outline-none focus:border-brand"
              />
              <div className="mt-1 text-[11.5px] text-muted-3">
                Forfyllt úr reiknivél (hlutur Atomika yfir {horizonYears} ár)
              </div>
            </div>

            <div className="mb-3">
              <ModalLabel>Líkur á sölu: {prob}%</ModalLabel>
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={prob}
                onChange={(e) => setProb(Number(e.target.value))}
                className="w-full"
              />
            </div>

            <div className="mb-4 rounded-[12px] bg-[#FEF1E3] px-4 py-3">
              <div className="text-[11.5px] font-semibold text-[#A9701F]">
                Vigtað virði (líkur × upphæð)
              </div>
              <div className="font-display text-[22px] font-extrabold text-[#B5651A]">
                {fmtKr(weighted)}
              </div>
            </div>

            {err && (
              <div className="mb-3 rounded-lg bg-danger-50 px-3 py-2 text-[12.5px] font-semibold text-danger">
                {err}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 rounded-[12px] border border-line bg-white px-4 py-2.5 text-[13px] font-bold text-muted hover:bg-canvas"
              >
                Hætta við
              </button>
              <button
                onClick={send}
                disabled={saving}
                className="flex-1 rounded-[12px] bg-[#F0871E] px-4 py-2.5 text-[13px] font-bold text-white hover:opacity-90 disabled:opacity-50"
              >
                {saving ? "Sendi…" : "Stofna sölutækifæri"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Fyrirtækjaleit (tengt CompanyRegistry API) ──────────────────────────────

function CustomerPicker({
  value,
  onNameChange,
  onPick,
}: {
  value: string;
  onNameChange: (name: string) => void;
  onPick: (c: PickedCompany) => void;
}) {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<Company[]>([]);
  const boxRef = useRef<HTMLDivElement>(null);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  useEffect(() => {
    if (!value.trim() || value.trim().length < 2) {
      setResults([]);
      return;
    }
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      api
        .searchCompanies(value, 8)
        .then((r) => setResults(r.results))
        .catch(() => setResults([]));
    }, 200);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [value]);

  const showDropdown = open && results.length > 0;

  return (
    <div ref={boxRef} className="relative">
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">
          ⌕
        </span>
        <input
          type="text"
          value={value}
          onChange={(e) => {
            onNameChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Leita eftir fyrirtæki eða heiti…"
          className="w-full rounded-lg border border-slate-200 py-2 pl-8 pr-3 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
          autoComplete="off"
          spellCheck={false}
        />
      </div>

      {showDropdown && (
        <div className="absolute z-40 mt-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
          <p className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            Fyrirtæki úr fyrirtækjaskrá
          </p>
          {results.map((c) => (
            <button
              key={c.kennitala}
              type="button"
              onClick={() => {
                onPick({ kennitala: c.kennitala, name: c.name });
                setOpen(false);
              }}
              className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-slate-50"
            >
              <span className="mt-1 inline-block h-2 w-2 shrink-0 rounded-full bg-amber-400" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-slate-800">
                  {c.name}
                </span>
                <span className="block truncate text-[11px] text-slate-400">
                  {[c.isatLabel, c.place].filter(Boolean).join(" · ")}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CompanyPicker({
  initialQuery,
  onPick,
}: {
  initialQuery?: string;
  onPick: (c: PickedCompany) => void;
}) {
  const [q, setQ] = useState(initialQuery ?? "");
  const [results, setResults] = useState<Company[]>([]);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      api
        .searchCompanies(q, 8)
        .then((r) => setResults(r.results))
        .catch(() => setResults([]));
    }, 200);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [q]);

  return (
    <div>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Leita að fyrirtæki…"
        className="w-full rounded-[12px] border border-line bg-white px-3.5 py-2.5 text-[14px] text-ink outline-none focus:border-brand"
      />
      {results.length > 0 && (
        <div className="mt-1.5 max-h-[180px] overflow-y-auto rounded-[12px] border border-line">
          {results.map((c) => (
            <button
              key={c.kennitala}
              onClick={() => onPick({ kennitala: c.kennitala, name: c.name })}
              className="flex w-full items-center justify-between border-b border-[#F5F8FC] px-3 py-2 text-left last:border-0 hover:bg-canvas"
            >
              <span className="truncate text-[13px] font-semibold text-ink">
                {c.name}
              </span>
              <span className="ml-2 flex-none text-[11.5px] text-muted-2">
                {c.place}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Vistaðar áætlanir ───────────────────────────────────────────────────────

function SavedScenarios({
  scenarios,
  onLoad,
  onDelete,
}: {
  scenarios: Scenario[];
  onLoad: (inputs: CalcInputs, customerName: string) => void;
  onDelete: (id: number) => void;
}) {
  if (scenarios.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 p-10 text-center">
        <div className="mb-3 text-4xl">📄</div>
        <h3 className="mb-1 font-semibold text-slate-800">
          Engar vistaðar áætlanir
        </h3>
        <p className="text-sm text-slate-500">
          Reiknaðu sparnað fyrir viðskiptavin í „Verðskrá“-flipanum og smelltu á
          „Vista áætlun“.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      {scenarios.map((s) => {
        const results = s.results;
        return (
          <div
            key={s.id}
            className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
          >
            <div className="mb-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="truncate font-semibold text-slate-900">
                  {s.customerName}
                </h3>
                <p className="mt-0.5 text-[11px] text-slate-400">
                  {new Date(s.createdAt).toLocaleDateString("is-IS", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </p>
              </div>
              <button
                onClick={() => onDelete(s.id)}
                className="p-1 text-slate-300 transition-colors hover:text-red-500"
                aria-label="Eyða"
                title="Eyða áætlun"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <dl className="flex-1 space-y-2 text-sm">
              <ScenarioStat
                label="Ávinningur viðsk. (heild)"
                value={fmtKr(results.totalCustomerBenefit)}
                strong
              />
              <ScenarioStat
                label="Ávinningur viðsk. 1. ár"
                value={fmtKr(results.year1CustomerBenefit)}
              />
              <ScenarioStat
                label="Tekjur Atomika (heild)"
                value={fmtKr(results.totalCompanyRevenue)}
              />
            </dl>

            {s.notes && (
              <p className="mt-3 line-clamp-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
                {s.notes}
              </p>
            )}

            <button
              onClick={() => onLoad(s.inputs, s.customerName)}
              className="mt-4 w-full rounded-lg py-2 text-sm font-medium text-white transition-colors"
              style={{ backgroundColor: ATOMIKA_BLUE }}
            >
              Opna í reiknivél
            </button>
          </div>
        );
      })}
    </div>
  );
}

function ScenarioStat({
  label,
  value,
  strong,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-slate-500">{label}</dt>
      <dd
        className={
          strong ? "font-bold text-slate-900" : "font-medium text-slate-700"
        }
      >
        {value}
      </dd>
    </div>
  );
}

// ─── Smá-íhlutir ─────────────────────────────────────────────────────────────

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-slate-600">
        {label}
      </span>
      {children}
    </label>
  );
}

function NumField({
  label,
  suffix,
  value,
  onChange,
}: {
  label: string;
  suffix?: string;
  value: number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  const display = Number.isFinite(value)
    ? Math.round(value)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ".")
    : "";

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleaned = e.target.value.replace(/[^\d]/g, "");
    e.target.value = cleaned;
    onChange(e);
  };

  return (
    <Field label={label}>
      <div className="relative">
        <input
          type="text"
          inputMode="numeric"
          value={display}
          onChange={handleChange}
          className="w-full rounded-lg border border-slate-200 px-3 py-2 pr-16 text-sm tabular-nums focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
        />
        {suffix && (
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-slate-400">
            {suffix}
          </span>
        )}
      </div>
    </Field>
  );
}

function SliderField({
  label,
  suffix,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  suffix?: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
}) {
  return (
    <Field label={label}>
      <div className="flex items-center gap-3">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="flex-1 accent-blue-600"
        />
        <span className="w-16 text-right text-sm font-semibold tabular-nums text-slate-800">
          {value}
          {suffix}
        </span>
      </div>
    </Field>
  );
}

function SortableResultCard({
  id,
  label,
  value,
  sub,
  accent,
  highlight,
}: {
  id: string;
  label: string;
  value: string;
  sub?: string;
  accent: string;
  highlight?: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    borderColor: highlight ? `${accent}40` : "#e2e8f0",
    background: highlight ? `${accent}0a` : "#fff",
    zIndex: isDragging ? 20 : undefined,
    boxShadow: isDragging ? "0 12px 28px rgba(15,23,42,0.18)" : undefined,
    opacity: isDragging ? 0.95 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="group relative cursor-grab touch-none select-none rounded-2xl border p-4 shadow-sm active:cursor-grabbing"
    >
      <span
        className="absolute right-3 top-3 text-slate-300 opacity-0 transition-opacity group-hover:opacity-100"
        aria-hidden
      >
        <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <circle cx="7" cy="5" r="1.4" />
          <circle cx="13" cy="5" r="1.4" />
          <circle cx="7" cy="10" r="1.4" />
          <circle cx="13" cy="10" r="1.4" />
          <circle cx="7" cy="15" r="1.4" />
          <circle cx="13" cy="15" r="1.4" />
        </svg>
      </span>
      <div className="mb-1.5 flex items-center gap-2">
        <span
          className="h-2 w-2 rounded-full"
          style={{ backgroundColor: accent }}
        />
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          {label}
        </p>
      </div>
      <p className="text-xl font-bold leading-tight tabular-nums text-slate-900">
        {value}
      </p>
      {sub && <p className="mt-1 text-[11px] text-slate-400">{sub}</p>}
    </div>
  );
}

function HeroStat({
  label,
  value,
  color,
  highlight,
}: {
  label: string;
  value: string;
  color?: string;
  highlight?: boolean;
}) {
  return (
    <div className="text-right">
      <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-blue-900/50">
        {label}
      </p>
      <p
        className="font-bold leading-none tabular-nums"
        style={{
          color: color ?? (highlight ? "#045AFF" : "#002B62"),
          fontSize: highlight ? "1.5rem" : "1.25rem",
        }}
      >
        {value}
      </p>
    </div>
  );
}

function LegendDot({
  color,
  label,
  dashed,
}: {
  color: string;
  label: string;
  dashed?: boolean;
}) {
  return (
    <span className="flex items-center gap-1.5">
      {dashed ? (
        <span
          className="h-0 w-4 border-t-2 border-dashed"
          style={{ borderColor: color }}
        />
      ) : (
        <span
          className="h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: color }}
        />
      )}
      {label}
    </span>
  );
}

function ModalLabel({ children }: { children: ReactNode }) {
  return (
    <div className="mb-1 text-[11.5px] font-bold uppercase tracking-[0.05em] text-muted-4">
      {children}
    </div>
  );
}
